import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Paper, Container, CircularProgress, Chip } from '@material-ui/core';
import { RiskAssessment, RiskLevel } from 'types/schema.type';

interface RiskBadgeProps {
  riskAssessment: RiskAssessment | null | undefined;
  loading?: boolean;
}

const riskColors: Record<RiskLevel, string> = {
  [RiskLevel.LOW]: '#4caf50',
  [RiskLevel.MODERATE]: '#ff9800',
  [RiskLevel.HIGH]: '#f44336',
  [RiskLevel.URGENT]: '#b71c1c',
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: 800,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  badge: {
    fontWeight: 'bold',
    color: '#fff',
  },
  summary: {
    marginTop: theme.spacing(1),
    lineHeight: 1.6,
  },
  findingsList: {
    marginTop: theme.spacing(1),
    paddingLeft: theme.spacing(2),
  },
  findingItem: {
    lineHeight: 1.6,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  disclaimer: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

export function RiskBadge({ riskAssessment, loading = false }: RiskBadgeProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  if (loading) {
    return (
      <Container className={classes.root} component={Paper}>
        <Typography variant="h6">{t('interpretation.risk')}</Typography>
        <div className={classes.loadingContainer}>
          <CircularProgress size={24} />
        </div>
      </Container>
    );
  }

  if (!riskAssessment) {
    return null;
  }

  const badgeColor = riskColors[riskAssessment.level] || riskColors[RiskLevel.LOW];

  return (
    <Container className={classes.root} component={Paper}>
      <div className={classes.header}>
        <Typography variant="h6">{t('interpretation.risk')}</Typography>
        <Chip
          label={riskAssessment.level}
          className={classes.badge}
          style={{ backgroundColor: badgeColor }}
          size="small"
        />
      </div>
      <Typography variant="body2" className={classes.summary}>
        {riskAssessment.summary}
      </Typography>
      {riskAssessment.keyFindings && riskAssessment.keyFindings.length > 0 && (
        <ul className={classes.findingsList}>
          {riskAssessment.keyFindings.map((finding, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={index}>
              <Typography variant="body2" className={classes.findingItem}>
                {finding}
              </Typography>
            </li>
          ))}
        </ul>
      )}
      <Typography variant="caption" className={classes.disclaimer}>
        {t('interpretation.aiDisclaimer')}
      </Typography>
    </Container>
  );
}
